/**
 * CategoryGroupsView — the "By Category" overview. Renders the scoped folder view
 * grouped into one card per smart category (plus an Uncategorized card for mail
 * with no category), each showing the message count and inline bulk actions
 * (archive / move / delete all). The card body opens the regular list filtered to
 * that category. Mirrors {@link SenderGroupsView}; the two header toggles narrow
 * BOTH the counts and the per-card action sets.
 *
 * Ordering: real categories follow the user's `sortOrder` by default (the "By
 * category" sort), with Date / Count as alternatives; the Uncategorized catch-all
 * always sorts last regardless of key or direction.
 */

import { useMemo, useState } from 'react';
import { format, isThisYear, isToday } from 'date-fns';
import {
  Archive,
  Trash2,
  Loader2,
  Tag,
  Star,
  Mail,
  Paperclip,
  ChevronRight,
  Inbox,
  ArrowUp,
  ArrowDown,
  Settings2,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { EmailCategoryGroupDto, EmailFolderDto } from '@/lib/api';

type SortKey = 'category' | 'date' | 'count';
type SortDir = 'asc' | 'desc';
interface CategorySort {
  key: SortKey;
  dir: SortDir;
}

// Per-device UI preference (like the By Sender sort) — persisted so it survives
// leaving/re-opening the overview and reloads. Default: the user's own category
// order, ascending (Primary → Feed → …), matching the server order.
const SORT_STORAGE_KEY = 'fias.email.categorySort';
const DEFAULT_CATEGORY_SORT: CategorySort = { key: 'category', dir: 'asc' };
/** Fallback swatch colour for a category with no colour / for Uncategorized. */
const UNCATEGORIZED_COLOR = '#9CA3AF';

export function loadCategorySort(): CategorySort {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<CategorySort>;
      // Coerce to the known enums so a stale/garbage value can't break sorting.
      return {
        key: p.key === 'date' ? 'date' : p.key === 'count' ? 'count' : 'category',
        dir: p.dir === 'asc' ? 'asc' : p.dir === 'desc' ? 'desc' : 'asc',
      };
    }
  } catch {
    // ignore malformed / unavailable storage
  }
  return DEFAULT_CATEGORY_SORT;
}

function saveCategorySort(sort: CategorySort): void {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
  } catch {
    // ignore unavailable storage
  }
}

function chipDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (isToday(d)) return format(d, 'h:mm a');
  return isThisYear(d) ? format(d, 'MMM d') : format(d, 'MMM d, yyyy');
}

/** A small pill toggle, matching the quick-filter chips in the message list. */
function ToggleChip({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon?: LucideIcon;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-accent'
      }`}
    >
      {Icon && <Icon className="h-3 w-3" />} {label}
    </button>
  );
}

/** A sort chip: shows the field, plus a direction arrow when it's the active key. */
function SortChip({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      title={`Sort by ${label.toLowerCase()}${active ? (dir === 'desc' ? ' (descending)' : ' (ascending)') : ''}`}
      className={`inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-accent'
      }`}
    >
      {label}
      {active &&
        (dir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
    </button>
  );
}

/** Stable per-card key — the category id, or a fixed token for Uncategorized. */
export function categoryGroupKey(g: EmailCategoryGroupDto): string {
  return g.categoryId ?? '__uncategorized__';
}

interface CategoryGroupsViewProps {
  groups: EmailCategoryGroupDto[] | undefined;
  loading: boolean;
  excludeStarred: boolean;
  excludeUnread: boolean;
  excludeAttachments: boolean;
  onToggleExcludeStarred: () => void;
  onToggleExcludeUnread: () => void;
  onToggleExcludeAttachments: () => void;
  onOpenCategory: (group: EmailCategoryGroupDto) => void;
  onArchive: (group: EmailCategoryGroupDto) => void;
  onDelete: (group: EmailCategoryGroupDto) => void;
  onMove: (group: EmailCategoryGroupDto, folderId: string) => void;
  /** Open the category-management dialog (create/rename/recolor/delete). */
  onManage: () => void;
  /** AI-categorize the uncategorized backlog (shown only on the Uncategorized card). */
  onBulkCategorize: () => void;
  /** Folders for the per-card move menu (single-account only). */
  folders: EmailFolderDto[];
  /** Whether per-card move is offered (single-account, non-unified view). */
  canMove: boolean;
}

export function CategoryGroupsView({
  groups,
  loading,
  excludeStarred,
  excludeUnread,
  excludeAttachments,
  onToggleExcludeStarred,
  onToggleExcludeUnread,
  onToggleExcludeAttachments,
  onOpenCategory,
  onArchive,
  onDelete,
  onMove,
  onManage,
  onBulkCategorize,
  folders,
  canMove,
}: CategoryGroupsViewProps) {
  const [sort, setSort] = useState<CategorySort>(() => loadCategorySort());
  const { key: sortKey, dir: sortDir } = sort;
  const cycleSort = (key: SortKey) => {
    setSort((prev) => {
      // Switching keys resets to that key's natural direction (ascending for the
      // category order, descending for date/count); clicking the active key flips.
      const natural: SortDir = key === 'category' ? 'asc' : 'desc';
      const next: CategorySort =
        key === prev.key
          ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
          : { key, dir: natural };
      saveCategorySort(next);
      return next;
    });
  };

  const sortedGroups = useMemo(() => {
    if (!groups) return groups;
    const arr = [...groups];
    arr.sort((a, b) => {
      // Uncategorized is a catch-all, not a real category → always last.
      const aUn = a.categoryId === null;
      const bUn = b.categoryId === null;
      if (aUn !== bUn) return aUn ? 1 : -1;
      const cmp =
        sortKey === 'date'
          ? (a.latestAt ? Date.parse(a.latestAt) : 0) - (b.latestAt ? Date.parse(b.latestAt) : 0)
          : sortKey === 'count'
            ? a.count - b.count
            : (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      // Stable tie-break by name so equal keys keep a deterministic order.
      const tie = cmp !== 0 ? cmp : a.name.localeCompare(b.name);
      return sortDir === 'asc' ? tie : -tie;
    });
    return arr;
  }, [groups, sortKey, sortDir]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header: title + sort + the action-scope toggles. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-4 py-2">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <Tag className="h-4 w-4 text-muted-foreground" /> By category
        </span>
        {groups && (
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {groups.length.toLocaleString()} categor{groups.length === 1 ? 'y' : 'ies'}
          </span>
        )}
        <button
          type="button"
          onClick={onManage}
          title="Manage categories"
          className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-accent"
        >
          <Settings2 className="h-3 w-3" /> Manage
        </button>
        <span className="text-[11px] text-muted-foreground">Sort:</span>
        <SortChip
          label="Category"
          active={sortKey === 'category'}
          dir={sortDir}
          onClick={() => cycleSort('category')}
        />
        <SortChip
          label="Date"
          active={sortKey === 'date'}
          dir={sortDir}
          onClick={() => cycleSort('date')}
        />
        <SortChip
          label="Count"
          active={sortKey === 'count'}
          dir={sortDir}
          onClick={() => cycleSort('count')}
        />
        <span className="flex-1" />
        {/* Keep the "Bulk actions skip:" label and its chips together as one unit
            so they wrap to the next line as a group rather than the label
            stranding on its own line above the chips. */}
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground">Bulk actions skip:</span>
          <ToggleChip
            active={excludeStarred}
            onClick={onToggleExcludeStarred}
            icon={Star}
            label="Starred"
          />
          <ToggleChip
            active={excludeUnread}
            onClick={onToggleExcludeUnread}
            icon={Mail}
            label="Unread"
          />
          <ToggleChip
            active={excludeAttachments}
            onClick={onToggleExcludeAttachments}
            icon={Paperclip}
            label="Attachments"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Grouping by category…
          </div>
        ) : !sortedGroups || sortedGroups.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No messages to group here.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {sortedGroups.map((g) => {
              const isUncategorized = g.categoryId === null;
              const swatch = g.color ?? UNCATEGORIZED_COLOR;
              return (
                <div
                  key={categoryGroupKey(g)}
                  className="group flex flex-col overflow-hidden rounded-lg border bg-card transition-colors hover:border-primary/40 hover:shadow-sm"
                >
                  {/* Body — opens the category's mail in the regular list view. */}
                  <button
                    type="button"
                    className="flex items-start gap-3 p-3 text-left"
                    title={`Show all mail in ${g.name}`}
                    onClick={() => onOpenCategory(g)}
                  >
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: `${swatch}22` }}
                      aria-hidden
                    >
                      {isUncategorized ? (
                        <Inbox className="h-4 w-4" style={{ color: swatch }} />
                      ) : (
                        <span
                          className="h-3.5 w-3.5 rounded-full"
                          style={{ backgroundColor: swatch }}
                        />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1">
                        <span
                          className={`truncate text-sm font-medium ${isUncategorized ? 'text-muted-foreground' : ''}`}
                        >
                          {g.name}
                        </span>
                        <span className="ml-auto shrink-0 rounded-full bg-accent px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-foreground">
                          {g.count.toLocaleString()}
                        </span>
                      </span>
                      <span className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                        <span className="shrink-0 tabular-nums">{chipDate(g.latestAt)}</span>
                        {g.latestSubject && (
                          <>
                            <span aria-hidden>·</span>
                            <span className="truncate">{g.latestSubject}</span>
                          </>
                        )}
                      </span>
                    </span>
                    <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>

                  {/* Bulk actions for the whole category. Wraps on narrow cards so
                      the Delete button never bleeds past the card's rounded edge. */}
                  <div className="flex flex-wrap items-center gap-1 border-t px-2 py-1.5">
                    {isUncategorized ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 px-2 text-xs"
                        title="AI-categorize these conversations"
                        onClick={onBulkCategorize}
                      >
                        <Sparkles className="h-3.5 w-3.5" /> Categorize
                      </Button>
                    ) : null}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 px-2 text-xs"
                      title={`Archive all (${g.count})`}
                      onClick={() => onArchive(g)}
                    >
                      <Archive className="h-3.5 w-3.5" /> Archive
                    </Button>
                    {canMove && (
                      <select
                        className="h-7 max-w-[7rem] rounded-md border bg-background px-1 text-xs"
                        aria-label={`Move all in ${g.name} to folder`}
                        value=""
                        onChange={(e) => {
                          const fid = e.target.value;
                          if (fid) onMove(g, fid);
                          e.currentTarget.value = '';
                        }}
                      >
                        <option value="">Move…</option>
                        {folders.map((f) => (
                          <option key={f.folderId} value={f.folderId}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto h-7 gap-1 px-2 text-xs text-destructive"
                      title={`Delete all (${g.count})`}
                      onClick={() => onDelete(g)}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!loading && groups && groups.length > 0 && (
          <p className="mt-4 flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
            <Inbox className="h-3 w-3" /> Click a category to see all of its mail · bulk actions can
            be undone
          </p>
        )}
      </div>
    </div>
  );
}
